import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import ChatListItem from "./ChatListItem";
import NewConversationForm from "./NewConversationForm";
import "./ChatList.css";
import { chatRoomsState, selectedChatRoomIdState, sortedChatRoomsState } from "../../state/chatState";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";
import CustomModal from "../../../../components/generic/custommodal/CustomModal";
import SearchInput from "../../../generic/elements/search/SearchInput";
import apiService from "../../../../service/apiservice/apiService";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";
import MemoryType from "../../../../service/memory/enum/MemoryType";

const CHAT_TYPE = {
  DIRECT: "direct",
  GROUP: "group",
};

const emptyConversation = { title: "", topic: "", selectedUsers: [], adminOnly: false };

const ChatList = () => {
  const chatList = useRecoilValue(sortedChatRoomsState);
  const userJson = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const setChatRooms = useSetRecoilState(chatRoomsState);
  const setSelectedRoomId = useSetRecoilState(selectedChatRoomIdState);
  const [, setSearchParams] = useSearchParams();
  const [isNewConversationModalOpen, setIsNewConversationModalOpen] = useState(false);
  const [newChatType, setNewChatType] = useState(CHAT_TYPE.GROUP);
  const [conversationDetails, setConversationDetails] = useState(emptyConversation);
  const [errors, setErrors] = useState({});
  const [conversationCreationLoading, setConversationCreationLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const skipInitialSearchFetchRef = useRef(true);

  const buildRoomsQueryParams = (query = searchQuery) => {
    const trimmedQuery = query?.trim();
    return {
      recipient: userJson?.userIdentifier,
      organizationIdentifier,
      ...(trimmedQuery ? { searchQuery: trimmedQuery } : {}),
    };
  };

  const fetchChatRooms = async (query = searchQuery) => {
    if (!userJson?.userIdentifier || !organizationIdentifier) {
      return;
    }

    const response = await apiService.get(
      "niramayafoundation/api/v1/communication/rooms",
      buildRoomsQueryParams(query),
    );

    if (Array.isArray(response)) {
      setChatRooms(response);
    }
  };

  useEffect(() => {
    if (skipInitialSearchFetchRef.current) {
      skipInitialSearchFetchRef.current = false;
      return;
    }

    fetchChatRooms(searchQuery);
  }, [searchQuery]);

  const closeNewConversationModal = () => {
    setIsNewConversationModalOpen(false);
    setNewChatType(CHAT_TYPE.GROUP);
    setConversationDetails(emptyConversation);
    setErrors({});
  };

  const handleChatTypeChange = (chatType) => {
    setNewChatType(chatType);
    setConversationDetails(emptyConversation);
    setErrors({});
  };

  const validateConversation = (values, chatType) => {
    const nextErrors = {};
    if (chatType === CHAT_TYPE.GROUP && !values?.title?.trim()) {
      nextErrors.title = "Title is required";
    }
    if (!values?.selectedUsers?.length) {
      nextErrors.selectedUsers =
        chatType === CHAT_TYPE.DIRECT
          ? "Select a user to message"
          : "At least one user is required";
    } else if (chatType === CHAT_TYPE.DIRECT && values.selectedUsers.length > 1) {
      nextErrors.selectedUsers = "Select only one user for a one-on-one chat";
    }
    return nextErrors;
  };
  
  const openChatRoom = (roomId) => {
    const normalizedRoomId = String(roomId);
    setSelectedRoomId(normalizedRoomId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("roomId", normalizedRoomId);
        return next;
      },
      { replace: true },
    );
  };

  const handleCreateConversation = async () => {
    const nextErrors = validateConversation(conversationDetails, newChatType);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (newChatType === CHAT_TYPE.DIRECT) {
      const selectedUser = conversationDetails.selectedUsers[0];
      if (selectedUser?.roomId) {
        openChatRoom(selectedUser.roomId);
        closeNewConversationModal();
        return;
      }
    }

    setConversationCreationLoading(true);
    const isGroup = newChatType === CHAT_TYPE.GROUP;
    const payload = {
      organizationIdentifier: userJson?.organizationIdentifier,
      chatType: newChatType,
      title: isGroup ? conversationDetails?.title : null,
      topic: isGroup ? conversationDetails?.topic : null,
      createdBy: userJson?.userIdentifier,
      memberUserIdentifiers: conversationDetails?.selectedUsers?.map(user => user?.userIdentifier),
      ...(isGroup && conversationDetails?.adminOnly ? { adminOnly: true } : {}),
    };

    try {
      const result = await apiService.post(
        "niramayafoundation/api/v1/communication/room",
        payload,
        "Chat room ready",
        "Could not start communication",
      );

      if (!result?.roomId) {
        return;
      }

      await fetchChatRooms();

      openChatRoom(result.roomId);
    } finally {
      setConversationCreationLoading(false);
      closeNewConversationModal();
    }
  };

  const footerButtons = (
    <div>
      <SecondaryButton buttonText="Cancel" handleOnClick={closeNewConversationModal} />
      <PrimaryButton buttonText="Create" loading={conversationCreationLoading} handleOnClick={handleCreateConversation} />
    </div>
  );

  return (
    <div className="chat-list-container">
      <div className="chat-list-header">
        <h3>Conversations</h3>
        {
          userJson?.jobTitle === "Volunteer Coordinator" && (
            <PrimaryButton
              buttonText="+ New"
              handleOnClick={() => setIsNewConversationModalOpen(true)}
            />
          )
        }
      </div>
      <div className="chat-list-search">
        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          cssClass="chat-list-search-input"
          searchPlaceholder="Search conversations..."
        />
      </div>
      <div className="chat-list-items chat-scroll-y">
        {chatList.map((chat) => (
          <ChatListItem key={chat.roomId} chat={chat} />
        ))}
      </div>
      {isNewConversationModalOpen && (
        <CustomModal
          title="New Conversation"
          isModalOpen={isNewConversationModalOpen}
          closeModal={closeNewConversationModal}
          footer={footerButtons}
          closable={false}
        >
          <div className="new-conversation-modal">
            <div className="new-conversation-tabs" role="tablist" aria-label="Conversation type">
              <button
                type="button"
                role="tab"
                aria-selected={newChatType === CHAT_TYPE.GROUP}
                className={`new-conversation-tab ${newChatType === CHAT_TYPE.GROUP ? "is-active" : ""}`}
                onClick={() => handleChatTypeChange(CHAT_TYPE.GROUP)}
              >
                Group Chat
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={newChatType === CHAT_TYPE.DIRECT}
                className={`new-conversation-tab ${newChatType === CHAT_TYPE.DIRECT ? "is-active" : ""}`}
                onClick={() => handleChatTypeChange(CHAT_TYPE.DIRECT)}
              >
                One-on-One
              </button>
            </div>
            <p className="new-conversation-tab-hint">
              {newChatType === CHAT_TYPE.DIRECT
                ? "Start a private conversation with one person."
                : "Create a group with a name and add multiple members."}
            </p>
            <NewConversationForm
              chatType={newChatType}
              conversationDetails={conversationDetails}
              setConversationDetails={setConversationDetails}
              errors={errors}
              setErrors={setErrors}
            />
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default ChatList;
